import type {
  DisclaimerPosition,
  DisclaimerSpec,
} from "@hightouch/lib/ad-studio/disclaimer/types";

/**
 * A user's edits in the bulk disclosure modal: only fields the user has
 * explicitly changed are set. Applied on top of each ad's own brand-resolved
 * spec, so an untouched control leaves every ad following its own default.
 */
export type DisclaimerOverride = {
  visible?: boolean;
  machineReadable?: boolean;
  position?: DisclaimerPosition;
};

/**
 * Display state for one control across the selected ads:
 *   - an explicit user override wins outright;
 *   - otherwise the shared value if every ad agrees;
 *   - otherwise `mixed` (render indeterminate / "more than one setting").
 */
export type FieldState<T> = { value: T | undefined; mixed: boolean };

export function resolveFieldState<T>(
  values: T[],
  overrideValue: T | undefined,
): FieldState<T> {
  if (overrideValue !== undefined) {
    return { value: overrideValue, mixed: false };
  }
  if (values.length === 0) {
    return { value: undefined, mixed: false };
  }
  const allSame = values.every((value) => value === values[0]);
  return allSame
    ? { value: values[0], mixed: false }
    : { value: undefined, mixed: true };
}

/** Apply the override's set fields on top of an ad's resolved spec. */
export function applyOverride(
  spec: DisclaimerSpec,
  override: DisclaimerOverride,
): DisclaimerSpec {
  return {
    ...spec,
    ...(override.visible !== undefined ? { visible: override.visible } : {}),
    ...(override.machineReadable !== undefined
      ? { machineReadable: override.machineReadable }
      : {}),
    ...(override.position !== undefined ? { position: override.position } : {}),
  };
}
